import type { ConfigurationEditDraft } from "@proof/runtime-config";

/** Rendering-shape guard only; the server owns configuration policy and validation. */
export function isConfigurationEditDraft(
  raw: unknown,
): raw is ConfigurationEditDraft {
  const record = (value: any) =>
    value !== null && typeof value === "object" && !Array.isArray(value);
  const text = (value: unknown) => typeof value === "string";
  const number = (value: unknown) =>
    typeof value === "number" && Number.isFinite(value);
  const strings = (value: unknown) => Array.isArray(value) && value.every(text);
  const ref = (value: any) =>
    record(value) &&
    ["retain", "replace"].includes(value.kind) &&
    text(value.slot);
  const provider = (value: any) =>
    record(value) &&
    text(value.endpoint) &&
    ref(value.token) &&
    number(value.timeoutMs) &&
    typeof value.allowHttp === "boolean";
  const v = raw as any;
  const placeholders = (value: any, path: string, depth: number): boolean => {
    if (depth > 32) return false;
    if (!record(value) && !Array.isArray(value)) return true;
    if (["managed", "file"].includes(value.kind)) return false;
    if (["retain", "replace"].includes(value.kind))
      return (
        ref(value) &&
        value.slot === path &&
        Object.keys(value).sort().join(",") === "kind,slot"
      );
    return Object.entries(value).every(([key, child]) =>
      placeholders(child, path ? `${path}.${key}` : key, depth + 1),
    );
  };
  return (
    record(v) &&
    placeholders(v, "", 0) &&
    text(v.name) &&
    ["development", "production"].includes(v.profile) &&
    record(v.http) &&
    text(v.http.publicOrigin) &&
    strings(v.http.embedOrigins) &&
    strings(v.http.trustedProxies) &&
    record(v.renderer) &&
    number(v.renderer.concurrency) &&
    number(v.renderer.leaseMs) &&
    record(v.security) &&
    number(v.security.sessionMs) &&
    ref(v.security.loginKey) &&
    ref(v.security.rateKey) &&
    record(v.queue) &&
    (v.queue.kind === "embedded" ||
      (v.queue.kind === "redis" &&
        ref(v.queue.url) &&
        (!v.queue.ca || ref(v.queue.ca)))) &&
    record(v.identity) &&
    (v.identity.kind === "development" ||
      (v.identity.kind === "http" && provider(v.identity))) &&
    (v.context === undefined || provider(v.context)) &&
    (v.delivery === undefined ||
      (provider(v.delivery) &&
        v.delivery.kind === "http" &&
        text(v.delivery.id) &&
        text(v.delivery.label)))
  );
}
